import { ReactNode, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * A titled group inside the side panel.
 *
 * Groups are flat — a small caps label and its controls — rather than boxed
 * cards, so a panel with five groups still reads as one column.
 */
export function Panel({
  title,
  children,
  defaultOpen = true,
  actions,
  collapsible = true,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  actions?: ReactNode;
  collapsible?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="group">
      <div className="group-title">
        {collapsible ? (
          <button
            className="icon-btn"
            style={{ width: 16, height: 16, marginLeft: -2 }}
            onClick={() => setOpen(!open)}
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          >
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : null}
        <span>{title}</span>
        <span className="spacer" />
        {actions ? <span className="row">{actions}</span> : null}
      </div>
      {open ? children : null}
    </section>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  title,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  title?: string;
}): JSX.Element {
  return (
    <label className="checkbox" title={title}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  title,
}: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  title?: string;
}): JSX.Element {
  return (
    <input
      className="input mono"
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      title={title}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(n);
      }}
    />
  );
}

/**
 * The dialog is built on Radix and lives in its own module; it is re-exported
 * here so the seven dialogs that already import it keep a single import site.
 */
export { Modal } from './Dialog';

/** Segmented control for small, mutually exclusive choices. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: ReactNode; title?: string }>;
  onChange: (value: T) => void;
}): JSX.Element {
  return (
    <div className="segmented">
      {options.map((o) => (
        <button
          key={o.value}
          className={value === o.value ? 'active' : ''}
          title={o.title}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Focus-on-mount text input used by inline rename and dialogs. */
export function AutoFocusInput(props: React.InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  return <input ref={ref} className="input" {...props} />;
}

/** Explanatory line under a control. Used instead of tooltips for anything
 *  a first-time user needs to know up front. */
export function Hint({ children }: { children: ReactNode }): JSX.Element {
  return <p className="hint-text">{children}</p>;
}
